/** Field rules that mirror backend/validation.py, so a form never submits something
 *  the API will reject and every message appears under the field it belongs to. */

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;
const DIGITS_RE = /^\+?\d{7,15}$/;
const DIVISION_RE = /^[A-Za-z0-9]{1,5}$/;
const ADMISSION_RE = /^[A-Za-z0-9][A-Za-z0-9/_-]{0,31}$/;

export const clean = (v) => String(v ?? '').trim().replace(/\s+/g, ' ');

export const nameError = (v, label, { required = true } = {}) => {
  const value = clean(v);
  if (!value) return required ? `${label} is required` : '';
  if (value.length < 2) return `${label} must be at least 2 characters`;
  if (value.length > 80) return `${label} must be 80 characters or fewer`;
  if (!/\p{L}/u.test(value)) return `${label} must contain at least one letter`;
  return '';
};

export const emailError = (v, label = 'Email', { required = true } = {}) => {
  const value = String(v ?? '').trim().toLowerCase();
  if (!value) return required ? `${label} is required` : '';
  if (value.length > 254 || !EMAIL_RE.test(value)) {
    return `Enter a valid ${label.toLowerCase()}, for example name@school.com`;
  }
  return '';
};

export const mobileError = (v, label = 'Mobile', { required = true } = {}) => {
  const value = String(v ?? '').trim().replace(/[\s\-().]/g, '').replace(/^00/, '+');
  if (!value) return required ? `${label} is required` : '';
  if (!DIGITS_RE.test(value)) {
    return `${label} must be 7 to 15 digits, with an optional country code like +91`;
  }
  return '';
};

export const textError = (v, label, { required = true, max = 80, min = 2 } = {}) => {
  const value = clean(v);
  if (!value) return required ? `${label} is required` : '';
  if (value.length < min) return `${label} must be at least ${min} characters`;
  if (value.length > max) return `${label} must be ${max} characters or fewer`;
  return '';
};

export const divisionError = (v) => {
  const value = clean(v);
  if (!value) return 'Division is required';
  if (!DIVISION_RE.test(value)) return 'Division must be up to 5 letters or digits, for example A or 10B';
  return '';
};

export const admissionError = (v) => {
  const value = String(v ?? '').trim();
  if (!value) return 'Admission number is required';
  if (value.length > 32) return 'Admission number must be 32 characters or fewer';
  if (!ADMISSION_RE.test(value)) {
    return 'Admission number can use letters, digits, hyphens, slashes and underscores';
  }
  return '';
};

export const selectError = (v, label) => (clean(v) ? '' : `${label} is required`);

/** Reads a field-tagged API error, falling back to a plain message. */
export const apiFieldError = (err, fallback = 'Something went wrong') => {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    return { field: detail.field || '', message: detail.message || fallback, rows: detail.rows };
  }
  if (typeof detail === 'string') return { field: '', message: detail };
  return { field: '', message: fallback };
};
